import {
  setPostHogConsent,
  usePostHog,
} from "shared-ui/composables/use-posthog.ts";
import type { CookieConsentConfig, CookieValue } from "vanilla-cookieconsent";

type AnalyticsConsentListener = (analyticsAccepted: boolean) => void;

const analyticsConsentListeners = new Set<AnalyticsConsentListener>();

export function onAnalyticsConsentChange(
  listener: AnalyticsConsentListener,
): () => void {
  analyticsConsentListeners.add(listener);
  return () => analyticsConsentListeners.delete(listener);
}

async function runOnConsent(consent: { cookie: CookieValue }): Promise<void> {
  const analyticsAccepted = consent.cookie.categories.includes("analytics");
  await usePostHog("on_reject");
  await setPostHogConsent(analyticsAccepted);

  for (const listener of analyticsConsentListeners) {
    listener(analyticsAccepted);
  }
}

export const configOptions: CookieConsentConfig = {
  cookie: {
    name: "cc_cookie",
  },

  categories: {
    necessary: {
      enabled: true, // this category is enabled by default
      readOnly: true, // this category cannot be disabled
    },
    analytics: {},
  },

  language: {
    default: "en",
    translations: {
      en: {
        consentModal: {
          title: "We use cookies",
          description: "We use cookies to keep things smooth (and tasty).",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Accept necessary only",
          showPreferencesBtn: "Manage Individual preferences",
        },
        preferencesModal: {
          title: "Manage cookie preferences",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Accept necessary only",
          savePreferencesBtn: "Accept current selection",
          closeIconLabel: "Close modal",
          sections: [
            {
              title: "Somebody said ... cookies?",
              description: "I want one!",
            },
            {
              title: "Strictly Necessary cookies",
              description:
                "These cookies are essential for the proper functioning of the website and cannot be disabled.",

              //this field will generate a toggle linked to the 'necessary' category
              linkedCategory: "necessary",
            },
            {
              title: "Performance and Analytics",
              description:
                "These cookies collect information about how you use our website, which we use to improve it. We do not sell your data to third parties.",
              linkedCategory: "analytics",
            },
            {
              title: "More information",
              description:
                'For more information, please <a href="https://www.controla.ai/privacy-policy">see the privacy policy</a>',
            },
          ],
        },
      },
    },
  },

  onConsent: (consent) => void runOnConsent(consent),
  onChange: (consent) => void runOnConsent(consent),
};
